import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Banknote, CreditCard, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'

export type StoreEventType = 'new' | 'cash' | 'card'

interface BannerData {
  id: number
  type: StoreEventType
  title: string
  body: string
  storeId: string
}

let nextId = 0
let pushFn: ((b: Omit<BannerData, 'id'>) => void) | null = null

/**
 * #968 店舗オーナー向けグローバル通知バナー。新規注文・現金会計リクエスト・カード
 * 決済完了を、どの店舗画面にいても知らせる。客側の OrderReadyBanner と同じ
 * imperative API（showStoreEventBanner）で、StoreNotifierMount の watcher から発火。
 * タップで該当店舗の注文管理画面へ遷移。App ルートに1つだけ常設。
 */
// eslint-disable-next-line react-refresh/only-export-components
export function showStoreEventBanner(b: Omit<BannerData, 'id'>): void {
  pushFn?.(b)
}

const STYLE: Record<StoreEventType, { icon: typeof Bell; ring: string; fg: string }> = {
  new: { icon: Bell, ring: 'bg-blue-500/15', fg: 'text-blue-600' },
  cash: { icon: Banknote, ring: 'bg-orange-500/15', fg: 'text-orange-600' },
  card: { icon: CreditCard, ring: 'bg-emerald-500/15', fg: 'text-emerald-600' },
}

export default function StoreEventBanner() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const [banner, setBanner] = useState<BannerData | null>(null)

  useEffect(() => {
    pushFn = (b) => {
      const id = ++nextId
      setBanner({ ...b, id })
      window.setTimeout(() => {
        setBanner((curr) => (curr?.id === id ? null : curr))
      }, 10000)
    }
    return () => { pushFn = null }
  }, [])

  if (!banner) return null

  const style = STYLE[banner.type]
  const Icon = style.icon

  return (
    <div className="fixed inset-x-0 top-0 z-[110] flex justify-center px-3 pt-[max(0.5rem,env(safe-area-inset-top))] pointer-events-none">
      <div className="pointer-events-auto w-full max-w-[430px] animate-slide-up rounded-2xl border border-gray-200 bg-white shadow-xl">
        <div className="flex items-start gap-3 px-4 py-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${style.ring}`}>
            <Icon size={20} className={`${style.fg} animate-pulse`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-bold text-text-primary">{banner.title}</p>
            <p className="mt-0.5 font-body text-xs text-text-secondary leading-relaxed">
              {banner.body}
            </p>
          </div>
          <button
            onClick={() => setBanner(null)}
            className="shrink-0 rounded-full p-1 tap-effect hover:bg-gray-100"
            aria-label="close"
          >
            <X size={16} className="text-text-tertiary" />
          </button>
        </div>
        <div className="flex gap-2 border-t border-gray-100 px-3 py-2">
          <button
            onClick={() => setBanner(null)}
            className="flex-1 rounded-lg py-2 font-body text-sm text-text-secondary tap-effect hover:bg-gray-50"
          >
            {t('storeOrders.notifyDismiss')}
          </button>
          <button
            onClick={() => {
              setBanner(null)
              navigate(`/store/orders/${banner.storeId}`)
            }}
            className="flex-1 rounded-lg bg-navy py-2 font-display text-sm font-semibold text-white tap-effect hover:bg-navy-light"
          >
            {t('storeOrders.notifyView')}
          </button>
        </div>
      </div>
    </div>
  )
}
